import AdvancedTaskDto from "../types/dtos/forms/AdvancedTaskDto";
import { AdvancedTaskQuestionMode } from "../types/dtos/forms/AdvancedTaskQuestionMode";
import { ClientTaskType } from "../types/dtos/tasks/advanced-tasks/ClientTaskType";
import { isReviewQuestionMode } from "./advancedTaskQuestionModeHelper";

export interface TaskWeightingTotalResult {
  total: number;
  formattedTotal: string;
  hasMissingOrInvalid: boolean;
  isExactly100: boolean;
  /** Active + Completed tasks included in the sum. */
  includedCount: number;
}

/** Normalise a saved weighting value: null / non-finite / negative → 0 contribution, flagged as missing. */
export function normaliseWeightingValue(
  value: number | null | undefined,
): { contribution: number; isMissingOrInvalid: boolean } {
  if (value === null || value === undefined || !Number.isFinite(value) || value < 0) {
    return { contribution: 0, isMissingOrInvalid: true };
  }
  return { contribution: value, isMissingOrInvalid: false };
}

/** Format for banner: whole numbers without decimals, otherwise 2dp, with % suffix. */
export function formatWeightingTotal(total: number): string {
  const rounded = Math.round(total * 100) / 100;
  if (Number.isInteger(rounded)) {
    return `${rounded}%`;
  }
  return `${rounded.toFixed(2)}%`;
}

/**
 * Text for the per-task weighting badge, or null when there is nothing meaningful to show
 * (task type has no weighting question configured, or the task has no saved value yet).
 * A saved zero is a deliberate choice by the author, so it is shown.
 */
export function getTaskWeightingBadgeText(
  taskType: ClientTaskType | null | undefined,
  weightingValue: number | null | undefined,
): string | null {
  if (!taskType || !taskType.weightingQuestionId) {
    return null;
  }

  if (typeof weightingValue !== "number" || !Number.isFinite(weightingValue)) {
    return null;
  }

  return formatWeightingTotal(weightingValue);
}

export function calculateTaskWeightingTotal(
  tasks: AdvancedTaskDto[] | null | undefined,
): TaskWeightingTotalResult {
  const included = (tasks ?? []).filter(
    (task) => task.status === "ACTIVE" || task.status === "COMPLETED",
  );

  let total = 0;
  let hasMissingOrInvalid = false;

  for (const task of included) {
    const { contribution, isMissingOrInvalid } = normaliseWeightingValue(
      task.weightingValue,
    );
    total += contribution;
    if (isMissingOrInvalid) {
      hasMissingOrInvalid = true;
    }
  }

  const formattedTotal = formatWeightingTotal(total);
  const rounded = Math.round(total * 100) / 100;

  return {
    total,
    formattedTotal,
    hasMissingOrInvalid,
    isExactly100: rounded === 100,
    includedCount: included.length,
  };
}

export function shouldShowTaskWeightingBanner(
  questionMode: AdvancedTaskQuestionMode | null | undefined,
  taskType: ClientTaskType | null | undefined,
): boolean {
  if (!taskType || !questionMode) {
    return false;
  }

  if (!isReviewQuestionMode(questionMode)) {
    return false;
  }

  if (taskType.weightingConfigIsInvalid) {
    if (typeof console !== "undefined" && console.debug) {
      console.debug(
        "Task type weighting config is invalid (multiple WEIGHTING-QUESTION-ID values). Banner hidden.",
        { taskTypeId: taskType.id },
      );
    }
    return false;
  }

  return !!taskType.weightingQuestionId;
}
